import { gsap } from 'gsap';

/** Fired when the splash screen begins its exit (slide up). */
export const SPLASH_EXIT_START = 'splash-exit-start';

/**
 * Hero content entrance: starts when the splash screen begins to move out,
 * so the user sees headline, subhead, CTAs, phone, and scroll indicator animate in.
 */
export class HeroContentReveal {
  private section: HTMLElement | null = null;
  private headline: HTMLElement | null = null;
  private subhead: HTMLElement | null = null;
  private ctas: HTMLElement | null = null;
  private phone: HTMLElement | null = null;
  private scrollIndicator: HTMLElement | null = null;
  private timeline: gsap.core.Timeline | null = null;
  private boundOnSplashExit: () => void;
  private fallbackTimer: number | null = null;
  private played = false;
  private reduceMotion = false;

  constructor() {
    this.boundOnSplashExit = this.onSplashExitStart.bind(this);

    this.reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.section = document.querySelector('.scroll-hero-section');
    if (!this.section) return;

    this.headline = this.section.querySelector('.hero-headline');
    this.subhead = this.section.querySelector('.hero-subhead');
    this.ctas = this.section.querySelector('.hero-ctas');
    this.phone = this.section.querySelector('.hero-phone-mockup');
    this.scrollIndicator = this.section.querySelector('.hero-scroll-indicator');

    if (this.reduceMotion) {
      gsap.set(
        [this.headline, this.subhead, this.ctas, this.phone, this.scrollIndicator].filter(Boolean),
        { opacity: 1, y: 0 }
      );
      return;
    }

    this.setInitialStates();
    this.setupSplashListener();
  }

  private setInitialStates(): void {
    const yHeadline = 32;
    const yRest = 24;
    if (this.headline) gsap.set(this.headline, { opacity: 0, y: yHeadline, force3D: true });
    if (this.subhead) gsap.set(this.subhead, { opacity: 0, y: yRest, force3D: true });
    if (this.ctas) gsap.set(this.ctas, { opacity: 0, y: yRest, force3D: true });
    if (this.phone) gsap.set(this.phone, { opacity: 0, y: yRest + 16, force3D: true });
    if (this.scrollIndicator) gsap.set(this.scrollIndicator, { opacity: 0, y: 8, force3D: true });
  }

  private setupSplashListener(): void {
    // Start hero motion when splash begins to move out
    window.addEventListener(SPLASH_EXIT_START, this.boundOnSplashExit);

    // If there is no splash (e.g. no splash markup), run after a short delay
    if (!document.documentElement.classList.contains('splash-active')) {
      this.fallbackTimer = window.setTimeout(() => this.playEntrance(), 400);
      return;
    }

    // Safety: if splash never fires (e.g. fails or missing), still animate after 10s
    this.fallbackTimer = window.setTimeout(() => {
      this.fallbackTimer = null;
      this.playEntrance();
    }, 10000);
  }

  private onSplashExitStart(): void {
    this.clearListeners();
    window.setTimeout(() => this.playEntrance(), 500);
  }

  private clearListeners(): void {
    window.removeEventListener(SPLASH_EXIT_START, this.boundOnSplashExit);
    if (this.fallbackTimer !== null) {
      window.clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  private playEntrance(): void {
    if (this.played) return;
    this.played = true;
    this.clearListeners();

    const tl = gsap.timeline({ defaults: { ease: 'power3.out', force3D: true } });

    if (this.headline) {
      tl.to(this.headline, { opacity: 1, y: 0, duration: 0.7 }, 0);
    }
    if (this.subhead) {
      tl.to(this.subhead, { opacity: 1, y: 0, duration: 0.5 }, 0.15);
    }
    if (this.ctas) {
      tl.to(this.ctas, { opacity: 1, y: 0, duration: 0.5 }, 0.25);
    }
    if (this.phone) {
      tl.to(this.phone, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, 0.35);
    }
    if (this.scrollIndicator) {
      tl.to(this.scrollIndicator, { opacity: 1, y: 0, duration: 0.4 }, 0.5);
    }

    tl.set([this.headline, this.subhead, this.ctas, this.phone, this.scrollIndicator].filter(Boolean), {
      clearProps: 'transform',
    });

    this.timeline = tl;
  }

  dispose(): void {
    this.clearListeners();
    this.timeline?.kill();
    this.timeline = null;
    this.section = null;
    this.headline = null;
    this.subhead = null;
    this.ctas = null;
    this.phone = null;
    this.scrollIndicator = null;
  }
}
